/**
 * <norvegr-bundle-drawer>
 *
 * Multi-step bundle configurator drawer.
 *
 * Couples: one duvet (a fill weight for each side), up to four pillows (two per partner, optional
 * single-pillow per partner) and a flat sheet whose size follows the duvet size.
 *
 * Lifetime (`config.mode === "lifetime"`): one duvet weight, the same pillow step as Couples
 * (four pillows selected by default), the same optional sheet, and a restoration line.
 * The restoration variant is the closest
 * price on that product to 10% of the duvet plus pillows. The highest variant is the cap.
 *
 * Everything is added to the cart as separate real products, linked by a shared `_bundle_id` property.
 *
 * Cart logic lives in `buildCartItems()` and `addToCart()` only.
 */
(() => {
  if (customElements.get('norvegr-bundle-drawer')) return;

  /**
   * Closest restoration variant to 10% of `basisCents`.
   * A tie picks the higher price. A target at or above the top variant uses that variant.
   */
  const matchRestorationVariant = (variants, basisCents) => {
    if (!variants || !variants.length || !basisCents) return null;
    const available = variants.filter((variant) => variant.available);
    const list = available.length ? available : variants;
    const target = Math.round(basisCents * 0.1);
    const cap = list.reduce((max, variant) => Math.max(max, variant.price), 0);
    if (target >= cap) return list.reduce((best, variant) => (variant.price > best.price ? variant : best));
    return list.reduce((best, variant) => {
      const bestGap = Math.abs(best.price - target);
      const gap = Math.abs(variant.price - target);
      if (gap < bestGap) return variant;
      if (gap === bestGap && variant.price > best.price) return variant;
      return best;
    });
  };

  const REGIONS = [
    { code: 'US', label: 'United States', name: 'US' },
    { code: 'UK', label: 'United Kingdom', name: 'UK' },
    { code: 'NOR', label: 'Norway', name: 'Norway' },
    { code: 'EUR', label: 'Europe', name: 'Europe' },
  ];

  const STEP_TITLES = {
    1: 'Step 1 - Select Bed Size',
    2: 'Step 2 - Dual Duvet Weights',
    3: 'Step 3 - Select Pillow Firmness',
    4: 'Step 4 - Add Bed Sheets',
    5: 'Step 5 - Review',
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const unique = (list) => Array.from(new Set(list.filter((item) => item !== undefined && item !== null && item !== '')));

  const countryToRegion = (iso) => {
    if (iso === 'GB' || iso === 'UK') return 'UK';
    if (iso === 'US') return 'US';
    if (iso === 'NO') return 'NOR';
    return 'EUR';
  };

  class NorvegrBundleDrawer extends HTMLElement {
    connectedCallback() {
      // Move to <body> so position: fixed is never trapped by a parent stacking context.
      if (this.parentElement !== document.body) {
        document.body.querySelectorAll(':scope > norvegr-bundle-drawer').forEach((el) => {
          if (el !== this && el.id === this.id) el.remove();
        });
        document.body.appendChild(this);
        return;
      }

      if (this.ready) return;
      this.ready = true;
      this.init();
    }

    disconnectedCallback() {
      if (this.parentElement === null) document.body.classList.remove('overflow-hidden');
    }

    /* ---------------------------------------------------------------- setup */

    init() {
      try {
        this.config = JSON.parse(this.querySelector('[data-bundle-config]').textContent);
      } catch (error) {
        console.error('norvegr-bundle: invalid config', error);
        return;
      }

      this.name = this.config.bundleName || 'Bundle';
      this.lifetime = this.config.mode === 'lifetime';
      this.dimensions = window.NorvegrDimensions || {};
      this.formatter = this.createFormatter();
      this.sheetSizeMap = this.parseSizeMap(this.config.sizeMap);

      this.products = {
        duvet: this.prepareProduct(this.config.products.duvet),
        pillow: this.prepareProduct(this.config.products.pillow),
        sheet: this.prepareProduct(this.config.products.sheet),
      };
      if (this.config.products.restoration) {
        this.products.restoration = this.prepareProduct(this.config.products.restoration);
      }

      this.inner = this.querySelector('[data-bundle-inner]');
      this.body = this.querySelector('[data-bundle-body]');
      this.cta = this.querySelector('[data-bundle-cta]');
      this.titleEl = this.querySelector('[data-bundle-title]');
      this.priceEl = this.querySelector('[data-bundle-price]');
      this.fromEl = this.querySelector('[data-bundle-from]');
      this.errorEl = this.querySelector('[data-bundle-error]');

      this.state = {
        step: 1,
        region: countryToRegion(this.config.country),
        size: null,
        left: null,
        right: null,
        pillowSize: null,
        p1a: null,
        p2a: null,
        p1b: null,
        p2b: null,
        partnerAOnePillow: false,
        partnerBOnePillow: false,
        weight: null,
        includeBedSheet: false,
      };

      this.regionPanelOpen = false;
      this.adding = false;

      this.bindEvents();
      this.update();
    }

    /**
     * Matches the store's own money format (symbol position, separators) by reading
     * a sample rendered by Liquid's `money` filter, e.g. "1.234.567,89 kr".
     */
    createFormatter() {
      const sample = String(this.config.moneySample || '');
      const match = sample.match(/^([^\d]*)([\d][\d.,'\s\u00a0\u202f]*\d)([^\d]*)$/);
      if (match) {
        const [, prefix, number, suffix] = match;
        const last = number.search(/[.,](?=\d{2}$)/);
        const decimals = last >= 0 ? 2 : 0;
        const decimalSeparator = last >= 0 ? number[last] : '';
        const integerPart = last >= 0 ? number.slice(0, last) : number;
        const thousandsMatch = integerPart.match(/[^\d]/);
        const thousandsSeparator = thousandsMatch ? thousandsMatch[0] : '';
        return { prefix, suffix, decimals, decimalSeparator, thousandsSeparator };
      }
      return null;
    }

    money(cents) {
      const amount = (cents || 0) / 100;
      const format = this.formatter;

      if (!format) {
        const currency = this.config.currency || 'USD';
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
      }

      const [whole, fraction] = amount.toFixed(format.decimals).split('.');
      const grouped = format.thousandsSeparator
        ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandsSeparator)
        : whole;
      const number = format.decimals ? `${grouped}${format.decimalSeparator}${fraction}` : grouped;
      return `${format.prefix}${number}${format.suffix}`;
    }

    parseSizeMap(raw) {
      const map = {};
      String(raw || '')
        .split(/\r?\n/)
        .forEach((line) => {
          const index = line.indexOf(':');
          if (index < 1) return;
          const key = line.slice(0, index).trim().toLowerCase();
          const value = line.slice(index + 1).trim();
          if (key && value) map[key] = value;
        });
      return map;
    }

    prepareProduct(product) {
      const names = (product.options || []).map((name) => String(name).toLowerCase());
      const sizeIdx = Math.max(0, names.findIndex((name) => name.includes('size')));
      product.sizeIdx = sizeIdx;
      product.otherIdx = names.length > 1 ? (sizeIdx === 0 ? 1 : 0) : -1;
      product.variants.forEach((variant) => {
        variant.regionList = String(variant.regions || '')
          .split(',')
          .map((code) => code.trim())
          .filter(Boolean);
      });
      product.otherValues =
        product.otherIdx >= 0 ? unique(product.variants.map((variant) => variant.options[product.otherIdx])) : [];
      return product;
    }

    /* ----------------------------------------------------------- data lookup */

    variantsIn(product, region) {
      return product.variants.filter((variant) => variant.regionList.includes(region));
    }

    sizesIn(product, region) {
      return unique(this.variantsIn(product, region).map((variant) => variant.options[product.sizeIdx]));
    }

    /** Couples duvet — Single not offered. Lifetime offers every size on the product. */
    duvetBundleSizes(region) {
      const sizes = this.sizesIn(this.products.duvet, region);
      if (this.lifetime) return sizes;
      return sizes.filter((size) => {
        const key = String(size).trim().toLowerCase();
        return key !== 'single' && key !== 'single bed';
      });
    }

    otherValuesFor(product, size, region) {
      return unique(
        this.variantsIn(product, region)
          .filter((variant) => variant.options[product.sizeIdx] === size)
          .map((variant) => variant.options[product.otherIdx])
      );
    }

    findVariant(product, size, other, region) {
      const matches = product.variants.filter(
        (variant) =>
          variant.options[product.sizeIdx] === size &&
          (other === null || other === undefined || variant.options[product.otherIdx] === other)
      );
      return matches.find((variant) => variant.regionList.includes(region)) || matches[0] || null;
    }

    weightRank(weight) {
      return this.products.duvet.otherValues.indexOf(weight);
    }

    heavierWeight(selection = this.state) {
      const { left, right } = selection;
      if (!left || !right) return left || right || null;
      return this.weightRank(left) >= this.weightRank(right) ? left : right;
    }

    sheetSizeFor(duvetSize) {
      return this.sheetSizeMap[String(duvetSize).toLowerCase()] || duvetSize;
    }

    resolvePillowSize() {
      const { region, pillowSize } = this.state;
      const sizes = this.sizesIn(this.products.pillow, region);
      if (pillowSize && sizes.includes(pillowSize)) return pillowSize;
      const wanted = String(this.config.defaultPillowSize || '').toLowerCase();
      return (
        sizes.find((size) => size.toLowerCase() === wanted) ||
        sizes.find((size) => size === 'Standard') ||
        sizes[0] ||
        null
      );
    }

    defaultPillowSize() {
      const sizes = this.sizesIn(this.products.pillow, this.state.region);
      const wanted = String(this.config.defaultPillowSize || '').toLowerCase();
      return sizes.find((size) => size.toLowerCase() === wanted) || sizes.find((size) => size === 'Standard') || sizes[0] || null;
    }

    /* ----------------------------------------------------------- dimensions */

    dimensionsFor(kind, variant, size, region) {
      const lookup = this.dimensions;
      const raw = variant && variant.dimensions ? String(variant.dimensions).trim() : '';
      if (raw) {
        return lookup.pickFromMultiRegionMetafield ? lookup.pickFromMultiRegionMetafield(raw, region) : raw;
      }
      if (kind === 'sheet') return (lookup.FLAT_SHEET_DIMENSIONS || {})[size] || '';
      const table = kind === 'duvet' ? lookup.DUVET_DIMENSIONS : lookup.PILLOW_DIMENSIONS;
      return (table && table[region] && table[region][size]) || '';
    }

    regionPrefix(region) {
      if (this.dimensions.regionToDisplayPrefix) return this.dimensions.regionToDisplayPrefix(region);
      return region === 'NOR' ? 'NO' : region === 'EUR' ? 'EU' : region;
    }

    dimensionsProperty(kind, variant, size, region) {
      const dims = this.dimensionsFor(kind, variant, size, region);
      if (kind === 'sheet') return [size, dims].filter(Boolean).join(' ');
      return [this.regionPrefix(region), size, dims].filter(Boolean).join(' ');
    }

    /* ---------------------------------------------------------------- state */

    pillowSlots(state = this.state) {
      return [
        { key: 'p1a', active: true },
        { key: 'p2a', active: !state.partnerAOnePillow },
        { key: 'p1b', active: true },
        { key: 'p2b', active: !state.partnerBOnePillow },
      ];
    }

    activePillowCount(state = this.state) {
      return this.pillowSlots(state).filter((slot) => slot.active).length;
    }

    pillowsStepComplete(state = this.state) {
      if (!state.pillowSize) return false;
      return this.pillowSlots(state).every((slot) => !slot.active || state[slot.key]);
    }

    isComplete(step) {
      const { size, left, right, weight } = this.state;
      if (step === 1) return Boolean(size);
      if (step === 2) return this.lifetime ? Boolean(size && weight) : Boolean(size && left && right);
      if (step === 3) return this.pillowsStepComplete();
      if (step === 4) return this.pillowsStepComplete();
      return true;
    }

    stepTitle(step) {
      if (!this.lifetime) return STEP_TITLES[step];
      return {
        1: 'Step 1 - Select Bed Size',
        2: 'Step 2 - Duvet Weight',
        3: 'Step 3 - Select Pillow Firmness',
        4: 'Step 4 - Add Bed Sheets',
        5: 'Step 5 - Review',
      }[step];
    }

    maxStep() {
      let max = 1;
      if (this.isComplete(1)) max = 2;
      if (max === 2 && this.isComplete(2)) max = 3;
      if (max === 3 && this.isComplete(3)) max = 4;
      if (max === 4 && this.isComplete(4)) max = 5;
      return max;
    }

    sanitize() {
      const state = this.state;
      const { duvet, pillow } = this.products;

      if (state.size && !this.duvetBundleSizes(state.region).includes(state.size)) state.size = null;

      if (state.size) {
        const weights = this.otherValuesFor(duvet, state.size, state.region);
        if (this.lifetime) {
          if (state.weight && !weights.includes(state.weight)) state.weight = null;
        } else {
          if (state.left && !weights.includes(state.left)) state.left = null;
          if (state.right && !weights.includes(state.right)) state.right = null;
        }
      }

      state.pillowSize = this.resolvePillowSize();
      if (state.pillowSize) {
        const firmness = this.otherValuesFor(pillow, state.pillowSize, state.region);
        this.pillowSlots(state).forEach(({ key, active }) => {
          if (!active) state[key] = null;
          else if (state[key] && !firmness.includes(state[key])) state[key] = null;
        });
      }

      if (state.step > this.maxStep()) state.step = this.maxStep();
    }

    update() {
      this.sanitize();
      this.renderSizes();
      this.renderRegion();
      this.renderWeights();
      this.renderPillows();
      this.renderBedSheets();
      this.renderRestoration();
      if (this.state.step === 5) this.renderReview();
      this.renderChrome();
    }

    set(patch) {
      Object.assign(this.state, patch);
      this.hideError();
      this.update();
    }

    /* ---------------------------------------------------------------- price */

    availablePrice(variants) {
      const prices = variants.filter((variant) => variant.available).map((variant) => variant.price);
      const all = variants.map((variant) => variant.price);
      const pool = prices.length ? prices : all;
      return pool.length ? Math.min(...pool) : 0;
    }

    // `selection` lets the hero price ask "what is the cheapest set?" with nothing chosen yet.
    partsFor(size, selection = this.state) {
      const { duvet, pillow, sheet } = this.products;
      const { region } = this.state;
      const merged = { ...this.state, ...selection };
      const pillowSize = merged.pillowSize || this.state.pillowSize;

      const weight = this.lifetime ? merged.weight || null : this.heavierWeight(merged);
      const duvetVariant = weight ? this.findVariant(duvet, size, weight, region) : null;
      const duvetPrice = duvetVariant
        ? duvetVariant.price
        : this.availablePrice(this.variantsIn(duvet, region).filter((variant) => variant.options[duvet.sizeIdx] === size));

      const pillowVariants = this.variantsIn(pillow, region).filter((variant) => variant.options[pillow.sizeIdx] === pillowSize);
      const pillowPrice = (firmness) => {
        const variant = firmness ? this.findVariant(pillow, pillowSize, firmness, region) : null;
        return variant ? variant.price : this.availablePrice(pillowVariants);
      };

      const pillowsTotal = this.pillowSlots(merged).reduce(
        (sum, slot) => sum + (slot.active ? pillowPrice(merged[slot.key]) : 0),
        0
      );

      const sheetVariant = this.findVariant(sheet, this.sheetSizeFor(size), null, region);
      const sheetPrice =
        merged.includeBedSheet === true && sheetVariant ? sheetVariant.price : 0;

      const basis = duvetPrice + pillowsTotal;
      const restoration = this.matchRestoration(basis);
      const restorationPrice = restoration ? restoration.price : 0;

      return {
        basis,
        restoration,
        total: basis + sheetPrice + restorationPrice,
      };
    }

    totalFor(size, selection = this.state) {
      return this.partsFor(size, selection).total;
    }

    matchRestoration(basisCents) {
      const product = this.products.restoration;
      if (!product) return null;
      return matchRestorationVariant(product.variants, basisCents);
    }

    // Cheapest possible bundle in the visitor's region (default pillow size, lightest options).
    fromPrice() {
      const sizes = this.duvetBundleSizes(this.state.region);
      if (!sizes.length) return 0;
      const selection = { pillowSize: this.defaultPillowSize() };
      if (this.lifetime) {
        selection.includeBedSheet = false;
        selection.weight = null;
        selection.p1a = null;
        selection.p2a = null;
        selection.p1b = null;
        selection.p2b = null;
      }
      return Math.min(...sizes.map((size) => this.totalFor(size, selection)));
    }

    estimate() {
      const { size, left, right, weight } = this.state;
      const sizes = size ? [size] : this.duvetBundleSizes(this.state.region);
      if (!sizes.length) return { cents: 0, complete: false };
      const cents = Math.min(...sizes.map((candidate) => this.totalFor(candidate)));
      const weightChosen = this.lifetime ? Boolean(weight) : Boolean(left && right);
      return { cents, complete: Boolean(size && weightChosen && this.pillowsStepComplete()) };
    }

    /* ------------------------------------------------------------ rendering */

    pillHtml({ attr, value, label, selected, disabled }) {
      return `<button type="button" class="norvegr-bundle__pill${selected ? ' is-selected' : ''}" role="radio" aria-checked="${selected ? 'true' : 'false'}" ${attr}="${escapeHtml(value)}"${disabled ? ' disabled' : ''}>${escapeHtml(label)}</button>`;
    }

    renderSizes() {
      const grid = this.querySelector('[data-size-grid]');
      const sizes = this.duvetBundleSizes(this.state.region);
      grid.innerHTML = sizes
        .map((size) => this.pillHtml({ attr: 'data-size-option', value: size, label: size, selected: this.state.size === size }))
        .join('');
    }

    renderRegion() {
      const region = this.state.region;
      const current = REGIONS.find((item) => item.code === region) || REGIONS[0];
      this.querySelector('[data-region-label]').textContent = `${current.name} bed sizing`;

      const toggle = this.querySelector('[data-region-toggle]');
      const panel = this.querySelector('[data-region-panel]');
      toggle.setAttribute('aria-expanded', String(this.regionPanelOpen));
      panel.hidden = !this.regionPanelOpen;
      toggle.classList.toggle('is-open', this.regionPanelOpen);

      this.querySelector('[data-region-grid]').innerHTML = REGIONS.map((item) =>
        this.pillHtml({ attr: 'data-region-option', value: item.code, label: item.label, selected: item.code === region })
      ).join('');
    }

    renderWeights() {
      const { duvet } = this.products;
      const { size, region } = this.state;
      const weights = size ? this.otherValuesFor(duvet, size, region) : [];

      const grids = this.lifetime ? ['single'] : ['left', 'right'];
      grids.forEach((side) => {
        const grid = this.querySelector(`[data-weight-grid="${side}"]`);
        if (!grid) return;
        const selected = this.lifetime ? this.state.weight : this.state[side];
        grid.innerHTML = weights
          .map((weight) => {
            const variant = this.findVariant(duvet, size, weight, region);
            return this.pillHtml({
              attr: 'data-weight-option',
              value: weight,
              label: weight,
              selected: selected === weight,
              disabled: !variant || !variant.available,
            }).replace('<button ', `<button data-side="${side}" `);
          })
          .join('');
      });

      const { left, right } = this.state;
      const note = this.querySelector('[data-mixed-note]');
      if (note) note.hidden = !(left && right && left !== right);
    }

    renderPillows() {
      const { pillow } = this.products;
      const { region, pillowSize } = this.state;
      const sizes = this.sizesIn(pillow, region);
      const defaultSize = this.defaultPillowSize();

      const select = this.querySelector('[data-pillow-size-select]');
      const placeholder = '<option value="">Need a different pillow size?</option>';
      const options = sizes
        .filter((size) => size !== defaultSize)
        .map((size) => {
          const variant = this.findVariant(pillow, size, null, region);
          const dims = this.dimensionsFor('pillow', variant, size, region);
          return `<option value="${escapeHtml(size)}">${escapeHtml(dims ? `${size} - ${dims}` : size)}</option>`;
        })
        .join('');
      select.innerHTML = placeholder + options;
      select.value = pillowSize && pillowSize !== defaultSize ? pillowSize : '';

      const includes = this.querySelector('[data-pillow-includes]');
      if (pillowSize) {
        const variant = this.findVariant(pillow, pillowSize, null, region);
        const metric = this.dimensionsFor('pillow', variant, pillowSize, region).replace(/\s*\(.*\)\s*$/, '');
        const count = this.activePillowCount();
        includes.textContent = `Includes ${count}x ${pillowSize} Pillows${metric ? ` (${metric})` : ''}`;
      } else {
        includes.textContent = '';
      }

      this.querySelectorAll('[data-pillow-second]').forEach((group) => {
        const side = group.dataset.pillowSecond;
        const hidden = side === 'a' ? this.state.partnerAOnePillow : this.state.partnerBOnePillow;
        group.hidden = hidden;
      });

      this.querySelectorAll('[data-pillow-second-toggle]').forEach((input) => {
        const side = input.dataset.pillowSecondToggle;
        const secondOn = side === 'a' ? !this.state.partnerAOnePillow : !this.state.partnerBOnePillow;
        input.checked = secondOn;
      });

      const firmness = pillowSize ? this.otherValuesFor(pillow, pillowSize, region) : [];
      this.pillowSlots().forEach(({ key, active }) => {
        const grid = this.querySelector(`[data-firmness-grid="${key}"]`);
        if (!grid) return;
        if (!active) {
          grid.innerHTML = '';
          return;
        }
        grid.innerHTML = firmness
          .map((value) => {
            const variant = this.findVariant(pillow, pillowSize, value, region);
            return this.pillHtml({
              attr: 'data-firmness-option',
              value,
              label: value,
              selected: this.state[key] === value,
              disabled: !variant || !variant.available,
            }).replace('<button ', `<button data-partner="${key}" `);
          })
          .join('');
      });
    }

    renderBedSheets() {
      const { sheet } = this.products;
      const { size, region, includeBedSheet } = this.state;
      const offer = this.querySelector('[data-sheet-offer]');
      const toggle = this.querySelector('[data-bed-sheet-toggle]');
      if (!offer || !toggle) return;

      toggle.checked = includeBedSheet === true;
      offer.hidden = !size;

      if (!size) return;

      const sheetSize = this.sheetSizeFor(size);
      const variant = this.findVariant(sheet, sheetSize, null, region);
      const titleEl = this.querySelector('[data-sheet-title]');
      const sizeEl = this.querySelector('[data-sheet-size]');
      const priceEl = this.querySelector('[data-sheet-price]');
      const thumbEl = this.querySelector('[data-sheet-thumb]');

      if (titleEl) titleEl.textContent = sheet.title || 'Bed sheets';
      if (sizeEl) {
        const dims = variant ? this.dimensionsProperty('sheet', variant, sheetSize, region) : sheetSize;
        sizeEl.textContent = dims ? `Size: ${dims}` : `Size: ${sheetSize}`;
      }
      if (priceEl) priceEl.textContent = variant ? this.money(variant.price) : '';
      if (thumbEl) {
        thumbEl.innerHTML = sheet.image
          ? `<img src="${escapeHtml(sheet.image)}" alt="" width="80" height="80" loading="lazy">`
          : '';
      }
    }

    renderRestoration() {
      const offer = this.querySelector('[data-restoration-offer]');
      if (!offer) return;
      const { restoration } = this.products;
      const { size } = this.state;
      offer.hidden = !size || !restoration;
      if (!size || !restoration) return;

      const parts = this.partsFor(size);
      const titleEl = this.querySelector('[data-restoration-title]');
      const descriptionEl = this.querySelector('[data-restoration-description]');
      const priceEl = this.querySelector('[data-restoration-price]');
      const thumbEl = this.querySelector('[data-restoration-thumb]');

      if (titleEl) titleEl.textContent = restoration.title || 'Restoration';
      if (descriptionEl) {
        const description = String(restoration.description || '').trim();
        descriptionEl.innerHTML = description;
        descriptionEl.hidden = !description;
      }
      if (priceEl) priceEl.textContent = parts.restoration ? this.money(parts.restoration.price) : '';
      if (thumbEl) {
        thumbEl.innerHTML = restoration.image
          ? `<img src="${escapeHtml(restoration.image)}" alt="" width="80" height="80" loading="lazy">`
          : '';
      }
    }

    reviewItem({ kind, heading, editStep, variant, size, lines, withDimensions = true }) {
      const product = this.products[kind];
      const region = this.state.region;
      const dims = withDimensions && size ? this.dimensionsProperty(kind, variant, size, region) : '';
      const image = product.image ? `<img src="${escapeHtml(product.image)}" alt="" width="80" height="80" loading="lazy">` : '';
      const rows = lines
        .concat(product.specifications ? [['Specifications', product.specifications]] : [])
        .concat(dims ? [['Dimensions', dims]] : [])
        .map(([key, value]) => `<li><span>${escapeHtml(key)}:</span> ${escapeHtml(value)}</li>`)
        .join('');

      return `
        <section class="norvegr-bundle__review-group">
          <div class="norvegr-bundle__review-head">
            <h3 class="norvegr-bundle__label">${escapeHtml(heading)}</h3>
            ${editStep ? `<button type="button" class="norvegr-bundle__edit" data-edit-step="${editStep}">Edit</button>` : ''}
          </div>
          <div class="norvegr-bundle__review-item">
            <div class="norvegr-bundle__review-thumb">${image}</div>
            <div class="norvegr-bundle__review-info">
              <p class="norvegr-bundle__review-title">${escapeHtml(product.title)}</p>
              <p class="norvegr-bundle__review-price">${escapeHtml(this.money(variant ? variant.price : 0))}</p>
              <ul class="norvegr-bundle__review-lines">${rows}</ul>
            </div>
          </div>
        </section>`;
    }

    reviewPillowRow(pillow, pillowSize, firmness, region, pillowLabel) {
      const variant = this.findVariant(pillow, pillowSize, firmness, region);
      const dims = this.dimensionsProperty('pillow', variant, pillowSize, region);
      const image = pillow.image ? `<img src="${escapeHtml(pillow.image)}" alt="" width="80" height="80" loading="lazy">` : '';
      const rows = [['Size', pillowSize], ['Firmness', firmness]]
        .concat(pillow.specifications ? [['Specifications', pillow.specifications]] : [])
        .concat(dims ? [['Dimensions', dims]] : [])
        .map(([key, value]) => `<li><span>${escapeHtml(key)}:</span> ${escapeHtml(value)}</li>`)
        .join('');

      return `
        <div class="norvegr-bundle__review-item">
          <div class="norvegr-bundle__review-thumb">${image}</div>
          <div class="norvegr-bundle__review-info">
            ${pillowLabel ? `<p class="norvegr-bundle__review-pillow-label">${escapeHtml(pillowLabel)}</p>` : ''}
            <p class="norvegr-bundle__review-title">${escapeHtml(pillow.title)}</p>
            <p class="norvegr-bundle__review-price">${escapeHtml(this.money(variant ? variant.price : 0))}</p>
            <ul class="norvegr-bundle__review-lines">${rows}</ul>
          </div>
        </div>`;
    }

    reviewPillowsGroup(pillow, pillowSize, region) {
      const active = (key) => this.pillowSlots().some((slot) => slot.key === key && slot.active);
      const partners = [
        {
          name: 'Partner A',
          slots: [
            { key: 'p1a', label: 'Pillow 1' },
            ...(active('p2a') ? [{ key: 'p2a', label: 'Pillow 2' }] : []),
          ],
        },
        {
          name: 'Partner B',
          slots: [
            { key: 'p1b', label: 'Pillow 1' },
            ...(active('p2b') ? [{ key: 'p2b', label: 'Pillow 2' }] : []),
          ],
        },
      ];

      const partnerBlocks = partners
        .map((partner) => {
          const showPillowLabels = partner.slots.length > 1;
          const rows = partner.slots
            .map(({ key, label }) =>
              this.reviewPillowRow(
                pillow,
                pillowSize,
                this.state[key],
                region,
                showPillowLabels ? label : null
              )
            )
            .join('');
          return `
            <div class="norvegr-bundle__review-partner">
              <p class="norvegr-bundle__review-partner-name">${escapeHtml(partner.name)}</p>
              ${rows}
            </div>`;
        })
        .join('');

      return `
        <section class="norvegr-bundle__review-group norvegr-bundle__review-group--pillows">
          <div class="norvegr-bundle__review-head">
            <h3 class="norvegr-bundle__label">Pillows</h3>
            <button type="button" class="norvegr-bundle__edit" data-edit-step="3">Edit</button>
          </div>
          <div class="norvegr-bundle__review-pillows">${partnerBlocks}</div>
        </section>`;
    }

    renderReview() {
      const container = this.querySelector('[data-review]');
      const { size, left, right, weight, pillowSize, region } = this.state;
      const weightReady = this.lifetime ? Boolean(weight) : Boolean(left && right);
      if (!(size && weightReady && pillowSize && this.pillowsStepComplete())) {
        container.innerHTML = '';
        return;
      }

      const { pillow, sheet } = this.products;
      const duvetWeight = this.lifetime ? weight : this.heavierWeight();
      const duvetVariant = this.findVariant(this.products.duvet, size, duvetWeight, region);
      const sheetSize = this.sheetSizeFor(size);
      const sheetVariant = this.findVariant(sheet, sheetSize, null, region);
      const parts = this.partsFor(size);

      const duvetLines = this.lifetime
        ? [['Size', size], ['Weight', weight]]
        : [['Size', size], ['Left side (Partner A)', left], ['Right side (Partner B)', right]];
      if (!this.lifetime && left !== right) duvetLines.push(['Priced at', `${duvetWeight} weight`]);

      const html = [
        `<section class="norvegr-bundle__review-group norvegr-bundle__review-group--size">
          <div class="norvegr-bundle__review-head">
            <h3 class="norvegr-bundle__label">Bed size:</h3>
            <button type="button" class="norvegr-bundle__edit" data-edit-step="1">Edit</button>
          </div>
          <p class="norvegr-bundle__review-size">${escapeHtml(size)}</p>
        </section>`,
        this.reviewItem({
          kind: 'duvet',
          heading: this.lifetime ? 'Duvet:' : 'Dual-fill duvet:',
          editStep: 2,
          variant: duvetVariant,
          size,
          lines: duvetLines,
        }),
        this.reviewPillowsGroup(pillow, pillowSize, region),
      ];

      if (this.state.includeBedSheet === true) {
        html.push(
          this.reviewItem({
            kind: 'sheet',
            heading: 'Bed sheet:',
            editStep: 4,
            variant: sheetVariant,
            size: sheetSize,
            lines: [['Size', sheetSize]],
          })
        );
      }

      if (parts.restoration) {
        html.push(
          this.reviewItem({
            kind: 'restoration',
            heading: 'Restoration:',
            editStep: 4,
            variant: parts.restoration,
            lines: [],
            withDimensions: false,
          })
        );
      }

      container.innerHTML = html.join('');
    }

    renderChrome() {
      const { step } = this.state;
      const max = this.maxStep();

      this.titleEl.textContent = this.stepTitle(step);

      this.querySelectorAll('[data-step-pill]').forEach((pill) => {
        const number = Number(pill.dataset.stepPill);
        pill.disabled = number > max;
        pill.classList.toggle('is-active', number === step);
        if (number === step) pill.setAttribute('aria-current', 'step');
        else pill.removeAttribute('aria-current');
      });

      this.querySelectorAll('[data-step]').forEach((panel) => {
        const active = Number(panel.dataset.step) === step;
        if (active && panel.hidden) {
          panel.hidden = false;
          panel.classList.remove('is-entering');
          void panel.offsetWidth; // restart the cross-fade animation
          panel.classList.add('is-entering');
        } else if (!active) {
          panel.hidden = true;
          panel.classList.remove('is-entering');
        }
      });

      const { cents, complete } = this.estimate();

      const fromCents = this.fromPrice();
      document.querySelectorAll(`[data-bundle-hero-price="${this.id}"]`).forEach((el) => {
        if (fromCents) el.textContent = this.money(fromCents);
      });

      this.priceEl.textContent = cents ? this.money(cents) : '';
      this.fromEl.hidden = complete || !cents;

      if (this.adding) {
        this.cta.textContent = 'Adding...';
        this.cta.disabled = true;
      } else if (step < 5) {
        this.cta.textContent = `Continue to step ${step + 1}`;
        this.cta.disabled = !this.isComplete(step);
      } else {
        this.cta.textContent = 'Add bundle to cart';
        this.cta.disabled = !(this.isComplete(1) && this.isComplete(2) && this.isComplete(3) && this.isComplete(4));
      }
    }

    /* --------------------------------------------------------------- events */

    bindEvents() {
      this.addEventListener('click', (event) => {
        const target = event.target;

        if (target.closest('[data-bundle-close]')) return this.close();

        // Guide links open the modal (handled in norvegr-modal.js); first match its region tab.
        if (target.closest('[data-norvegr-modal-open="size-guide"]')) this.syncGuideRegion();

        const sizeOption = target.closest('[data-size-option]');
        if (sizeOption) return this.set({ size: sizeOption.dataset.sizeOption });

        const regionToggle = target.closest('[data-region-toggle]');
        if (regionToggle) {
          this.regionPanelOpen = !this.regionPanelOpen;
          return this.renderRegion();
        }

        const regionOption = target.closest('[data-region-option]');
        if (regionOption) {
          this.regionPanelOpen = false;
          return this.set({ region: regionOption.dataset.regionOption });
        }

        const weightOption = target.closest('[data-weight-option]');
        if (weightOption) {
          if (this.lifetime) return this.set({ weight: weightOption.dataset.weightOption });
          return this.set({ [weightOption.dataset.side]: weightOption.dataset.weightOption });
        }

        const firmnessOption = target.closest('[data-firmness-option]');
        if (firmnessOption) return this.set({ [firmnessOption.dataset.partner]: firmnessOption.dataset.firmnessOption });

        const pill = target.closest('[data-step-pill]');
        if (pill) return this.goTo(Number(pill.dataset.stepPill));

        const edit = target.closest('[data-edit-step]');
        if (edit) return this.goTo(Number(edit.dataset.editStep));

        if (target.closest('[data-bundle-cta]')) return this.onCta();
      });

      this.addEventListener('change', (event) => {
        const select = event.target.closest('[data-pillow-size-select]');
        if (select) this.set({ pillowSize: select.value || null });

        const secondToggle = event.target.closest('[data-pillow-second-toggle]');
        if (secondToggle) {
          const side = secondToggle.dataset.pillowSecondToggle;
          const onePillow = !secondToggle.checked;
          if (side === 'a') {
            return this.set({ partnerAOnePillow: onePillow, ...(onePillow ? { p2a: null } : {}) });
          }
          if (side === 'b') {
            return this.set({ partnerBOnePillow: onePillow, ...(onePillow ? { p2b: null } : {}) });
          }
        }

        const sheetToggle = event.target.closest('[data-bed-sheet-toggle]');
        if (sheetToggle) {
          return this.set({ includeBedSheet: sheetToggle.checked });
        }
      });

      // keydown (not keyup): an open guide modal handles Escape first and stops it here.
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    syncGuideRegion() {
      document
        .querySelectorAll(`norvegr-modal[data-modal-id="size-guide"] input[type="radio"][value="${this.state.region}"]`)
        .forEach((input) => {
          input.checked = true;
        });
    }

    goTo(step) {
      if (!step || step < 1 || step > this.maxStep()) return;
      this.state.step = step;
      this.hideError();
      this.update();
      if (this.body) this.body.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onCta() {
      const { step } = this.state;
      if (step < 5) {
        if (this.isComplete(step)) this.goTo(step + 1);
        return;
      }
      this.addToCart();
    }

    /* ---------------------------------------------------------- open / close */

    open(trigger) {
      if (this.classList.contains('active')) return;
      this.trigger = trigger || document.activeElement;
      this.hideError();

      setTimeout(() => this.classList.add('animate', 'active'));
      document.body.classList.add('overflow-hidden');

      this.addEventListener(
        'transitionend',
        () => {
          if (typeof trapFocus === 'function') trapFocus(this.inner, this.querySelector('[data-bundle-close]'));
        },
        { once: true }
      );
    }

    close() {
      if (!this.classList.contains('active')) return;
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.trigger);
    }

    /* ------------------------------------------------------------------ cart */

    buildCartItems() {
      const { duvet, pillow, sheet } = this.products;
      const { size, left, right, pillowSize, region } = this.state;

      const bundleId = `${this.lifetime ? 'lifetime' : 'couples'}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
      const common = { _bundle_id: bundleId, Bundle: this.name };
      const withSpecs = (product, properties) =>
        product.specifications ? { ...properties, Specifications: product.specifications } : properties;

      const duvetWeight = this.lifetime ? this.state.weight : this.heavierWeight();
      const duvetVariant = this.findVariant(duvet, size, duvetWeight, region);
      const sheetSize = this.sheetSizeFor(size);
      const sheetVariant = this.findVariant(sheet, sheetSize, null, region);
      const parts = this.partsFor(size);

      const duvetProperties = this.lifetime
        ? { Weight: this.state.weight, Dimensions: this.dimensionsProperty('duvet', duvetVariant, size, region) }
        : {
            'Left side weight': left,
            'Right side weight': right,
            Dimensions: this.dimensionsProperty('duvet', duvetVariant, size, region),
          };

      const items = [
        {
          id: duvetVariant.id,
          quantity: 1,
          properties: withSpecs(duvet, { ...common, ...duvetProperties }),
        },
      ];

      const cartPillowLabels = {
        p1a: ['Partner A', 'Pillow 1'],
        p2a: ['Partner A', 'Pillow 2'],
        p1b: ['Partner B', 'Pillow 1'],
        p2b: ['Partner B', 'Pillow 2'],
      };

      this.pillowSlots().forEach(({ key, active }) => {
        if (!active) return;
        const firmness = this.state[key];
        const variant = this.findVariant(pillow, pillowSize, firmness, region);
        const [partner, pillowNum] = cartPillowLabels[key];
        items.push({
          id: variant.id,
          quantity: 1,
          properties: withSpecs(pillow, {
            ...common,
            'Pillow for': `${partner} (${pillowNum})`,
            Dimensions: this.dimensionsProperty('pillow', variant, pillowSize, region),
          }),
        });
      });

      if (this.state.includeBedSheet === true && sheetVariant) {
        items.push({
          id: sheetVariant.id,
          quantity: 1,
          properties: withSpecs(sheet, {
            ...common,
            Dimensions: this.dimensionsProperty('sheet', sheetVariant, sheetSize, region),
          }),
        });
      }

      if (this.lifetime) {
        const restoration = this.products.restoration;
        items.push({
          id: parts.restoration ? parts.restoration.id : null,
          quantity: 1,
          properties: restoration
            ? withSpecs(restoration, {
                ...common,
                _Basis: this.money(parts.basis),
              })
            : { ...common },
        });
      }

      // Wanted order in the cart and checkout (top to bottom):
      //   duvet, pillows (Partner A then B), flat sheet, restoration.
      // Shopify lists the most recently added line first, so the items are sent in reverse.
      // If the cart ever shows them upside down, set this to false.
      const CART_LISTS_NEWEST_FIRST = true;

      return CART_LISTS_NEWEST_FIRST ? items.reverse() : items;
    }

    async addToCart() {
      if (this.adding) return;
      this.hideError();

      let items;
      try {
        items = this.buildCartItems();
      } catch (error) {
        return this.showError('Please complete every step before adding the bundle to your cart.');
      }

      if (items.some((item) => !item.id)) {
        return this.showError('Sorry, one of the selected items is unavailable. Please change your selection.');
      }

      this.adding = true;
      this.renderChrome();

      const cartDrawer = document.querySelector('cart-drawer');
      const sections = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map((section) => section.id)
        : [];
      const base = ((window.routes && window.routes.cart_add_url) || '/cart/add').replace(/\.js$/, '');

      try {
        const response = await fetch(`${base}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Sorry, we could not add this bundle to your cart.');
        }

        this.close();
        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
          cartDrawer.renderContents(data);
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.adding = false;
        this.renderChrome();
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      if (this.state.step !== 4) this.goTo(4);
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  customElements.define('norvegr-bundle-drawer', NorvegrBundleDrawer);

  // Open from any trigger: <button data-norvegr-bundle-open="DRAWER_ID">
  const openDrawer = (id, trigger) => {
    const drawer = document.getElementById(id);
    if (drawer && typeof drawer.open === 'function') drawer.open(trigger);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-norvegr-bundle-open]');
    if (!trigger) return;
    event.preventDefault();
    openDrawer(trigger.dataset.norvegrBundleOpen, trigger);
  });

  document.addEventListener('norvegr:open-bundle', (event) => {
    if (event.detail && event.detail.id) openDrawer(event.detail.id, event.detail.trigger);
  });
})();
